import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, cancel, select, takeLatest } from 'redux-saga/effects';

import {
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  VorschlagsdatenRequestPayload,
} from '@ps20-frontend/shop-integration-redux';
import {
  Berufsrisikogruppe as BffBerufsrisikogruppe,
  Fachbaustein,
  Feature,
  PersonAntrag,
  UriDatenAntrag,
} from '@ps20/bff-models';
import {
  App,
  convertToBffLaufzeit,
  getFachschluessel,
  handleLinieFromVorvertrag,
  LeistungEigenschaft,
  mapToBffVorvertragsNummer,
  Produktlinie,
} from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import dayjs from 'dayjs';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  getSelectedProduktlinie,
  mapGesamtbeitragIfJaehrlichAndBeitragIsWrong,
} from '@ps20-frontend/preis-lib';
import { convertDate, DATEFORMAT_SHORT_ISO, ZAHL_STRING } from '@ps20-frontend/utils';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { IAppState } from '../appState';
import { Vp } from '../model/vp';
import { isDateInRange } from '../selector/produkt.selector';

export function* watchVorschlagdatenChanged(): SagaIterator {
  yield takeLatest(GlobalActions.VORSCHLAGDATEN_REQUESTED, safeSaga(vorschlagdatenSaga, App.UNF));
}

export function* vorschlagdatenSaga(action: GlobalAction<VorschlagsdatenRequestPayload>): SagaIterator {
  try {
    const { angebotsIds } = action.payload;
    const { angebotsId }: IAppState = yield select();

    if (
      !angebotsIds.some(angebot => angebot.appId === App.UNF && angebot.angebotsId === angebotsId)
    ) {
      return yield cancel();
    }
    const state: IAppState = yield select();
    const globalPut = GlobalPut.getInstance();

    const ausgewaehlteProduktlinie = getSelectedProduktlinie(state.produkt);
    const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);

    const vorschlagdaten: UriDatenAntrag = {
      spartenType: state.produkt.spartenType,
      angebotsId: state.angebotsId,
      angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
      jahresbeitrag: state.jahresbeitrag,
      savedJahresbeitragMitRabatt: state.savedJahresbeitragMitRabatt,
      gesamtpreisNachZahlweise: mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.UNF,
      ),
      savedGesamtbeitragMitRabatt: state.savedGesamtbeitragMitRabatt,
      antragsId: state.antragsnummer,
      sicherungsglaeubiger: null,
      hspLfnr: state.produkt.hspLfnr,
      produkt: {
        beginn: null,
        ablauf: null,
        selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
        produktline: ausgewaehlteProduktlinie.produktName,
        kurztext: ausgewaehlteProduktlinie.kurztext,
        langtext: ausgewaehlteProduktlinie.langtext,
        fachschluessel: setFachschluessel(state, ausgewaehlteProduktlinie, ZAHL_STRING),
      },
      rbdMax: ausgewaehlteProduktlinie.rbdMax,
      rbdAktuell: state.taasInfo.rbdNachlass,
      treuebonus: state.taasInfo.tbNachlass,
      savedRbdNachlass: state.taasInfo.savedRbdNachlass,
      savedTbNachlass: state.taasInfo.savedTbNachlass,
      vorteilsrabatt: state.taasInfo.vorteilsrabatt,
      versichertePerson: convertToBffVersichertePerson(
        state.vp,
        state.vertragsoptionen.beginnDatum,
      ),
      berufsrisikogruppe: (BffBerufsrisikogruppe as Record<string, BffBerufsrisikogruppe>)[state.vp.berufsrisikogruppe],
      profiId: state.vp?.profiSport?.sportId,
      profiSportRisk: state.vp?.profiSport?.profiSportRisk,
      preisverzeichnis: ausgewaehlteProduktlinie.preisverzeichnis,
      vorversicherung: [],
      antragSpeichernDatum: dayjs().format(DATEFORMAT_SHORT_ISO),
      laufzeit: convertToBffLaufzeit(state.vertragsoptionen.vertragslaufzeit),
      rabattstufe: state.rabattstufe,
      geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
      vorvertragsnummern: mapToBffVorvertragsNummer(state.vertragsoptionen.vorvertragsNummern),
      vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
      zielmarktAbweichung: false,
      hasDynamik: false,
      kooperation: state.vertragsoptionen.selectedKooperation,
    };

    handleLinieFromVorvertrag(vorschlagdaten, ausgewaehlteProduktlinie, state.vertragsoptionen);

    yield call(
      [globalPut, globalPut.put],
      new GlobalActions().vorschlagsdatenGenerated(vorschlagdaten),
    );
  } catch (e) {
    PS20Logger.error(App.UNF, e as Error, 'VorschlagdatenSaga.vorschlagdatenSaga()');
  }
}

function setFachschluessel(
  state: IAppState,
  selectedProduktlinie: Produktlinie,
  regExpForSumme: string,
): Array<Fachbaustein> {
  const fachschluessel = getFachschluessel(state.produkt).filter(
    fschl =>
      selectedProduktlinie.leistungen.find(
        leistung => fschl.fachschluessel === leistung.fachschluessel,
      )?.eigenschaft !== LeistungEigenschaft.EXKLUSIVE,
  );
  fachschluessel.forEach(fschl => {
    fschl.summe =
      selectedProduktlinie.leistungen
        .find(leistung => fschl.fachschluessel === leistung.fachschluessel)
        ?.eigenschaft?.toString() ||
      selectedProduktlinie.zusatzoptionen
        .find(zusatzoption => fschl.fachschluessel === zusatzoption.fachschluessel)
        ?.eigenschaft?.toString();
    if (!fschl.summe?.match(new RegExp(regExpForSumme))) {
      fschl.summe = null;
    }
  });
  return fachschluessel;
}

function convertToBffVersichertePerson(vp: Vp, beginndatum: string): PersonAntrag {
  return {
    personenID: vp.kundenverwaltungID,
    kundennummer: vp.kundennummer,
    geburtsdatum: vp.geburtsdatum ? convertDate(vp.geburtsdatum) : null,
    anrede: vp.anrede,
    nachname: vp.nachname,
    vorname: vp.vorname,
    isVersicherungsnehmer: vp.isVn,
    akadgrad: vp.akadgrad,
    adelsbez: vp.adelsbez,
    isMinderjaehrig: isDateInRange(vp.geburtsdatum, beginndatum, 0, 16),
    adresse: {
      plz: vp.adresse.plz,
      ort: vp.adresse.ort,
      strasse: vp.adresse.strasse,
      hausnummer: vp.adresse.hausnummer,
      ortsteil: vp.adresse.ortsteil,
      hausnrzus: vp.adresse.hausnrzus,
      land: {
        wert: vp.adresse.land?.wert || 'DE',
        text: vp.adresse.land?.text || 'Deutschland',
      },
    },
  } as PersonAntrag;
}
